'use client';

import type { ReactNode } from 'react';
import { randomId } from '@mantine/hooks';
import { modals } from '@mantine/modals';

import { Box } from '../box';
import { Button } from '../button';
import { Group } from '../layout/group';
import type { ModalProps } from '../modal';
import modalClasses from '../modal/modal.module.css';
import { PromptBody, PromptTitle, WarningIcon } from './open-confirm-prompt';

interface SaveBeforeLeaveContentProps {
  message: ReactNode;
  leaveLabel: string;
  stayLabel: string;
  saveLabel: string;
  onLeave: () => void;
  /** Saves, and leaves if that worked. The prompt closes once it settles either way. */
  onSave: () => Promise<void>;
}

interface SaveBeforeLeavePrompt extends SaveBeforeLeaveContentProps {
  title: string;
  variant?: ModalProps['variant'];
  /** Called however the prompt closes. */
  onClose: () => void;
}

function SaveBeforeLeaveContent(
  props: SaveBeforeLeaveContentProps & { modalId: string; isSaving?: boolean },
) {
  const { modalId, message, leaveLabel, stayLabel, saveLabel, onLeave, onSave, isSaving } = props;
  const close = () => modals.close(modalId);
  const leave = () => {
    onLeave();
    close();
  };
  const saveAndLeave = async () => {
    // A save that lands after the prompt was dismissed would still leave, so nothing dismisses it.
    // Saving is kept in the modal rather than in state: a prompt the save opens over this one, which
    // Mantine shows in its place, unmounts this content until it closes.
    modals.updateModal({
      modalId,
      children: <SaveBeforeLeaveContent {...props} isSaving />,
      closeButtonProps: { disabled: true },
      closeOnClickOutside: false,
      closeOnEscape: false,
    });
    try {
      await onSave();
    } finally {
      close();
    }
  };

  // Mantine's ConfirmModal shape, which modal.module.css lays out.
  return (
    <>
      <Box>
        <PromptBody>{message}</PromptBody>
      </Box>
      <Group className={modalClasses.footer} gap="xs">
        <Button disabled={isSaving} mr="auto" onClick={leave} variant="subtle">
          {leaveLabel}
        </Button>
        <Button disabled={isSaving} onClick={close} variant="default">
          {stayLabel}
        </Button>
        <Button loading={isSaving} onClick={() => void saveAndLeave()}>
          {saveLabel}
        </Button>
      </Group>
    </>
  );
}

/**
 * The leave prompt with a third, primary way out: save, then go. Laid out like `openConfirmPrompt`,
 * whose two-button confirm can't carry it.
 */
export const openSaveBeforeLeavePrompt = ({
  title,
  variant,
  onClose,
  ...content
}: SaveBeforeLeavePrompt) => {
  const modalId = randomId();
  modals.open({
    modalId,
    title: <PromptTitle icon={<WarningIcon />}>{title}</PromptTitle>,
    children: <SaveBeforeLeaveContent {...content} modalId={modalId} />,
    size: 'lg',
    variant,
    onClose,
  });
};
